import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import env from "../../../../config/env"
import Icons from "../../../../components/icons";
import Analytics from "./assets/Analytics.png";
import SearchIcon from "@mui/icons-material/Search";
import { useUserInfo } from "../../../../context/user";
import { ArrowRightAlt } from "@mui/icons-material";
import AddUserModal from "../team/component/AddUserModal";
import { useUsersContext } from "../../../../context/usersContext";
import { useActionData, useNavigate } from "react-router-dom";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { Link } from "react-router-dom";
import { IconButton } from "@mui/material";
import { useAppServices } from "../../../../hook/services";
import { Toast } from "../../../../components/Toast/Toast";
import Nango from "@nangohq/frontend";

const NangoIntegration = ({ locationId, userInfo }) => {
  const [waiting, setWaiting] = useState(false);
  const [error, setError] = useState(null);
  const [integrations, setIntegrations] = useState([]);
  const [connectedInt, setConnectedInt] = useState([]);
  const [loadingProvider, setLoadingProvider] = useState(null);
  const [confirmDisconnect, setConfrimDisconnect] = useState(false);
  const [disconnectProvider, setDisconnectProvider] = useState({});
  const [disconnecting, setDisconnecting] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const BASE_URL = `${env.API_URL}/v1`;
  const userActiveLocation = useUserInfo()[4];
  const saveActiveLocation = useUserInfo()[5];
  const [openTeamModal, setOpenTeamModal] = useState(false);
  const { products, locationUsers, agency } = useUsersContext();
  const activeProduct = products?.filter((item) => item.activePlan)?.[0];
  const navigate = useNavigate();
  const [toast, setToast] = useState({ message: '', subMessage: '', visible: false, })
  const NANGO_API_HOST = env.NANGO_API_HOST || "https://api.nango.dev";
  const NANGO_CONNECT_UI_HOST = env.NANGO_CONNECT_UI_HOST || "https://connect.nango.dev";
  const hasConnectedRef = useRef(false);

  const nango = new Nango({
    host: NANGO_API_HOST,
    apiURL: NANGO_API_HOST
  });

  const launchOAuthPopup = async (integration) => {
    setLoadingProvider(integration);
    setError(null);

    try {
      const sessionResponse = await axios.post(
        `${BASE_URL}/integrations/nango/createConnectSession`,
        {
          end_user: {
            id: userInfo?.userId,
            email: userInfo?.email,
            display_name: userInfo?.userName || "User",
          },
          allowed_integrations: [integration.integration_id || integration.provider],
        },
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      if (!sessionResponse.data?.success || !sessionResponse.data?.data?.token) {
        throw new Error(
          sessionResponse.data?.message ||
          sessionResponse.data?.error ||
          "Failed to get session token"
        );
      }

      const sessionToken = sessionResponse.data.data.token;
      const connect = nango.openConnectUI({
        baseURL: NANGO_CONNECT_UI_HOST,
        apiURL: NANGO_API_HOST,
        onEvent: async (event) => {
          if (event.type === "close") {
            setLoadingProvider(null);
            if (!hasConnectedRef.current)
              setError({
                [integration.provider]: "Connection flow was closed"
              });
          } else if (event.type === "connect") {
            try {
              const { connectionId } = event.payload;
              const sendConnectionToBackend = await axios.post(`${BASE_URL}/integrations/nango/storeSession`, { provider: integration.provider, connection_id: connectionId, profile_id: locationId, user_id: userInfo?.userId, agency_id: userActiveLocation?.agency_id, ...(integration?.integration_id ? { integration_id: integration.integration_id } : {}) });
              if (sendConnectionToBackend.data.success) {
                hasConnectedRef.current = true;
                setLoadingProvider(null);
                fetchIntegrations();
              }
              else {
                setLoadingProvider(null);
                setError({
                  [integration.provider]:
                    sendConnectionToBackend.data.message || "Failed to store connection"
                });
              }
            } catch (err) {
              setLoadingProvider(null);
              setError({
                [integration.provider]: "Something went wrong while saving connection"
              });
            }
          }
        },
      });

      connect.setSessionToken(sessionToken);
    } catch (err) {
      console.error("Nango connection error:", err);
      setError({ [integration.provider]: err.response?.data?.message || err.message || "Failed to connect" });
      setLoadingProvider(null);
    }
    setTimeout(() => { setError(null) }, 3000)
  };

  const currentURL = window.location.href;
  const urlObject = new URL(currentURL);
  const domain = urlObject.hostname;

  const fetchIntegrations = async () => {
    setWaiting(true);
    try {
      const response = await axios.get(
        `${BASE_URL}/integrations/available?location_id=${locationId}&user_id=${userInfo?.userId}&agency_id=${userActiveLocation?.agency_id}`
      );
      setIntegrations(response.data?.data || []);
      setConnectedInt(response?.data?.connected || []);
      setWaiting(false);
    } catch (error) {
      console.error("Failed to fetch integrations:", error);
      setError("Failed to fetch integrations.");
    }
  };

  useEffect(() => {
    if (userActiveLocation?.agency_id)
      fetchIntegrations();
  }, [userActiveLocation]);

  let connectionInterval = null;
  const checkConnection = async () => {
    const response = await axios.get(
      `${BASE_URL}/accounts/location?id=${locationId}`
    );
    if (response?.data?.data?.nango_connected) {
      clearInterval(connectionInterval);
      setLoadingProvider(null);
      fetchIntegrations();
    }
  };

  const handleDisconnection = async () => {
    setDisconnecting(true);
    console.log("disconnectProvider", disconnectProvider);
    // return
    try {
      const response = await axios.post(
        `${BASE_URL}/integrations/disconnect?profile_id=${locationId}&provider=${disconnectProvider?.provider}&user_id=${userInfo?.userId}${disconnectProvider?.integration_id !== undefined ? `&integration_id=${disconnectProvider?.integration_id}` : ""}`
      );
      setDisconnectProvider({});
      setDisconnecting(false);
      setConfrimDisconnect(false);
      fetchIntegrations();
    } catch (error) {
      console.error("Failed to fetch integrations:", error);
      setError("Failed to fetch integrations.");
    }
  };

  const getConnectedIntegration = (integration) => {
    const connected = connectedInt.find((item) => item.provider === integration?.provider);
    if (integration?.integration_id && connected && integration?.integration_id === connected?.integration_id) {
      return connected
    }
    if (!integration?.integration_id && connected) {
      return connected
    }
    return null
  };

  const handleGHLLocationRefresh = () => {
    const scope =
      "users.readonly workflows.readonly contacts.readonly contacts.write locations/customFields.readonly locations/customFields.write businesses.readonly businesses.write calendars.readonly calendars.write calendars/events.readonly calendars/events.write calendars/groups.readonly calendars/groups.write calendars/resources.readonly calendars/resources.write campaigns.readonly conversations.readonly conversations/message.readonly conversations.write conversations/message.write conversations/reports.readonly objects/schema.readonly objects/schema.write objects/record.readonly objects/record.write courses.write courses.readonly forms.readonly forms.write invoices.readonly invoices.write invoices/schedule.readonly invoices/schedule.write invoices/template.readonly invoices/template.write links.readonly lc-email.readonly links.write locations.readonly locations/customValues.readonly locations/customValues.write locations/tasks.readonly locations/tasks.write locations/tags.readonly locations/tags.write locations/templates.readonly medias.readonly medias.write funnels/redirect.readonly funnels/page.readonly funnels/funnel.readonly funnels/pagecount.readonly funnels/redirect.write opportunities.readonly opportunities.write payments/orders.readonly payments/orders.write payments/integration.readonly payments/integration.write payments/transactions.readonly payments/subscriptions.readonly payments/custom-provider.readonly payments/custom-provider.write products.readonly products.write products/prices.readonly products/prices.write products/collection.readonly products/collection.write socialplanner/oauth.readonly socialplanner/oauth.write socialplanner/post.readonly socialplanner/post.write socialplanner/account.readonly socialplanner/account.write socialplanner/csv.readonly socialplanner/csv.write socialplanner/category.readonly socialplanner/tag.readonly store/shipping.readonly store/shipping.write store/setting.readonly store/setting.write surveys.readonly users.write blogs/post.write blogs/post-update.write blogs/check-slug.readonly blogs/category.readonly blogs/author.readonly";

    const refreshUrl = `https://marketplace.gohighlevel.com/oauth/chooselocation?response_type=code&redirect_uri=https://${domain}/app-integrations/location&client_id=${env.GHL.CLIENT_ID}&scope=${scope}&state=${locationId}`;

    window.open(refreshUrl, "_blank");
  };

  const handleDisconnectGHL = async () => {
    setDisconnecting(true)
    const response = await axios.put(
      `${BASE_URL}/accounts/disconnect-ghl?_id=${userActiveLocation?._id}`
    );

    if (response?.data?.success) {
      saveActiveLocation(response?.data?.data)
      setDisconnecting(false);
      setConfrimDisconnect(false)
      setDisconnectProvider({})
    }

    if (!response?.data?.success) {
      setDisconnecting(false);
      setConfrimDisconnect(false)
      setDisconnectProvider({})
      setToast({ message: response?.data?.message, visible: true, subMessage: "" })
    }

  }

  return (
    <>
      <div>
        <h2 className="text-xl font-semibold font-inter mb-9">Integrations</h2>
        <div className="relative mb-11">
          <input
            type="text"
            className={`inputsearch !w-[740px] `}
            placeholder="Search integrations"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value?.toLowerCase())}
          />

          <span className="absolute top-3 left-3">
            <SearchIcon />
          </span>
        </div>
      </div>

      <div style={{ marginBottom: "20px" }}>
        <b> No access?</b> Ask someone with permission or{" "}
        <u>
          <b style={{ cursor: "pointer" }} onClick={() => navigate('/app/settings', { state: { tab: "Team" } })}>
            invite a teammate
          </b>
        </u>
        {' '}to help connect it.
      </div>
      <div style={{ marginBottom: "20px" }}>
        Can't find the integration you need? <u><b><a href="https://www.make.com/en/integrations/your-ai" target="_blank">Maybe* App on Make</a></b></u> connects you to over <b>2,000+ platforms</b> and we are adding more daily.
      </div>

      <div className="flex flex-wrap">
        {!waiting ? (
          <>
            {integrations
              ?.filter((item) => {
                const providerName = item?.provider?.toLowerCase();
                const displayName = item?.display_name?.toLowerCase();

                // If searching for "instagram", show Meta/Facebook integration
                if (
                  searchQuery === "instagram" &&
                  providerName === "facebook"
                ) {
                  return true;
                }

                // Default search by provider name or display name
                return (
                  providerName?.includes(searchQuery) ||
                  displayName?.includes(searchQuery)
                );
              })
              ?.map((integration) => (
                <div
                  className="w-[320px] h-[326px] !border-[#D9D9D9] rounded border mr-4 mb-4 p-7 "
                  key={integration.provider}
                >
                  <div className="flex flex-col justify-between h-full">
                    {getConnectedIntegration(integration)
                      ?.provider === integration?.provider ? (
                      <div className="py-[5px] test-[12px] px-4 w-fit h-fit rounded-full bg-[#CFF7D3] ml-auto">
                        Connected
                      </div>
                    ) : (
                      <div className="py-[5px] test-[12px] px-4 w-fit h-fit rounded-full bg-[#E6E6E6] text-[#757575] ml-auto">
                        Disconnected
                      </div>
                    )}
                    <div className="flex items-center gap-2">
                      <div className="border rounded !border-[#D9D9D9] w-[50px] h-[50px] flex items-center justify-center">
                        <img
                          src={
                            integration?.provider === "google-analytics"
                              ? Analytics
                              : integration?.provider?.toLowerCase() ===
                                "fireflies"
                                ? `https://www.google.com/s2/favicons?sz=64&domain_url=fireflies.ai`
                                : integration?.provider?.toLowerCase() ===
                                  "roller"
                                  ? `https://www.google.com/s2/favicons?sz=64&domain_url=https://mysupport.roller.software/`
                                  : integration?.provider?.toLowerCase() ===
                                    "google-ads"
                                    ? `https://www.google.com/s2/favicons?sz=64&domain_url=ads.google.com`
                                    : integration?.provider?.toLowerCase() ===
                                      "facebook"
                                      ? `https://www.google.com/s2/favicons?sz=64&domain_url=meta.com`
                                      : integration?.provider?.toLowerCase() === 'microsoft-teams' ? 'https://www.google.com/s2/favicons?sz=64&domain_url=teams.live.com' : integration?.provider?.toLowerCase()?.includes('fathom') ? "https://www.google.com/s2/favicons?sz=64&domain_url=fathom.ai" : `https://www.google.com/s2/favicons?sz=64&domain_url=${integration?.provider
                                        ?.split("-")?.[0]
                                        ?.toLowerCase()}.com`
                          }
                          alt={integration?.display_name}
                          className="w-[30px] h-[30px]"
                        />
                      </div>

                      <div className="text-[22px] font-bold text-wrap">
                        {integration?.provider?.toLowerCase() === "facebook"
                          ? "Meta"
                          : integration?.display_name?.length > 15
                            ? integration.display_name.slice(0, 15) + "..."
                            : integration?.display_name}
                      </div>
                    </div>

                    <div className="font-normal">
                      <div className="h-fit flex items-center">
                        <h2
                          className=" block leading-[140%]"
                          style={{ fontSize: "20px" }}
                        >
                          Connect
                          {getConnectedIntegration(integration)
                            ?.provider === integration?.provider
                            ? "ed"
                            : ""}{" "}
                          to{" "}
                          {integration.provider === "google-drive"
                            ? " Google (Docs, Sheets, Slides)"
                            : integration?.provider?.toLowerCase() ===
                              "facebook"
                              ? "Meta"
                              : integration.display_name}
                        </h2>
                      </div>
                      {integration?.article_url && (
                        <a
                          href={integration.article_url}
                          target="_blank"
                          rel="noreferrer"
                          underline="none"
                          style={{
                            fontSize: "12px",
                            display: "flex",
                            alignItems: "center",
                            gap: "4px",
                          }}
                        >
                          <span style={{ color: "#b1b1b1" }}>Learn more</span>
                          <OpenInNewIcon
                            fontSize="inherit"
                            sx={{ color: "#b1b1b1" }}
                          />
                        </a>
                      )}
                    </div>
                    <div className="flex flex-col">
                      {getConnectedIntegration(integration)
                        ?.provider === integration?.provider ? (
                        <div className="flex items-center gap-2 font-normal text-[12px]">
                          <span className="w-[10px] h-[10px] rounded-full bg-[#CFF7D3]"></span>
                          <strong style={{ textTransform: "capitalize" }}>
                            {getConnectedIntegration(integration)
                              ?.userName !== null
                              ? getConnectedIntegration(integration)
                                ?.userName
                              : ""}
                          </strong>
                        </div>
                      ) : (
                        <div className="flex items-center gap-2 !font-normal text-[12px]">
                          <span className="w-[10px] h-[10px] rounded-full bg-[#FDD3D0]"></span>
                          <strong style={{ textTransform: "capitalize" }}>
                            No Account found
                          </strong>
                        </div>
                      )}

                      <div className="flex mt-5 w-full self-end !flex-col">
                        <div
                          key={integration.provider}
                          style={{ marginBottom: "10px" }}
                        >
                          {integration.provider === "monday" || integration.provider === 'microsoft-teams' ? (
                            <div className="flex gap-2 w-[260px]">
                              {getConnectedIntegration(integration)?.provider === integration?.provider ? (
                                <button
                                  onClick={() => {
                                    setConfrimDisconnect(true);
                                    setDisconnectProvider({
                                      provider: integration.provider,
                                      name: integration?.display_name,
                                      integration_id: integration?.integration_id,
                                    });
                                  }}
                                  className="px-10 py-2 text-xs  !border-[#D9D9D9] w-full h-[42px] rounded border  uppercase font-bold"
                                >
                                  {(loadingProvider?.integration_id && loadingProvider?.integration_id === integration?.integration_id && loadingProvider?.provider === integration.provider) || (!loadingProvider?.integration_id && loadingProvider?.provider === integration?.provider)
                                    ? `Disconnecting to ${integration.display_name}...`
                                    : `Disconnect`}
                                </button>
                              ) : (
                                <>{integration.show_install_button && <button
                                  onClick={() => {
                                    if (integration.provider === 'monday')
                                      window.open(env.REACT_APP_MONDAY_URL, '_blank')
                                    if (integration?.provider === 'microsoft-teams')
                                      window.open(env.REACT_APP_TEAMS_URL, '_blank')
                                  }}
                                  className="px-4 py-2 text-xs bg-white border flex flex-col items-center justify-center border-black w-full h-[42px] text-nowrap rounded text-black font-bold"
                                >
                                  <span style={{ color: "#000000", fontSize: "12px" }} className="text-nowrap">
                                    Step 1
                                  </span>
                                  INSTALL APP
                                </button>}
                                  <button
                                    onClick={() => launchOAuthPopup(integration)}
                                    disabled={
                                      (loadingProvider?.integration_id && loadingProvider?.integration_id === integration?.integration_id && loadingProvider?.provider === integration.provider) || (!loadingProvider?.integration_id && loadingProvider?.provider === integration?.provider)
                                        ? true
                                        : false
                                    }
                                    className="px-10 py-2 text-xs bg-[#1E1E1E] flex flex-col items-center justify-center w-full h-[42px] rounded text-white font-bold"
                                  >
                                    {integration?.show_install_button && <span style={{ color: "#fff", fontSize: "12px" }}>
                                      Step 2
                                    </span>}
                                    {(loadingProvider?.integration_id && loadingProvider?.integration_id === integration?.integration_id && loadingProvider?.provider === integration.provider) || (!loadingProvider?.integration_id && loadingProvider?.provider === integration?.provider)
                                      ? `Connecting to ${integration.display_name}...`
                                      : `CONNECT`}
                                  </button>
                                </>
                              )}
                            </div>
                          ) : getConnectedIntegration(integration)
                            ?.provider === integration?.provider ? (
                            <button
                              onClick={() => {
                                setConfrimDisconnect(true);
                                setDisconnectProvider({
                                  provider: integration.provider,
                                  name: integration?.display_name,
                                  integration_id: integration?.integration_id,
                                });
                              }}
                              className="px-10 py-2 text-xs  !border-[#D9D9D9] w-[260px] h-[42px] rounded border  uppercase font-bold"
                            >
                              {(loadingProvider?.integration_id && loadingProvider?.integration_id === integration?.integration_id && loadingProvider?.provider === integration.provider) || (!loadingProvider?.integration_id && loadingProvider?.provider === integration?.provider)
                                ? `Disconnecting to ${integration.display_name}...`
                                : `Disconnect`}
                            </button>
                          ) : (
                            <button
                              onClick={() =>
                                launchOAuthPopup(integration)
                              }
                              disabled={
                                (loadingProvider?.integration_id && loadingProvider?.integration_id === integration?.integration_id && loadingProvider?.provider === integration.provider) || (!loadingProvider?.integration_id && loadingProvider?.provider === integration?.provider)
                                  ? true
                                  : false
                              }
                              className="px-10 py-2 text-xs bg-[#1E1E1E] w-[260px] h-[42px] rounded text-white uppercase font-bold"
                            >
                              {(loadingProvider?.integration_id && loadingProvider?.integration_id === integration?.integration_id && loadingProvider?.provider === integration.provider) || (!loadingProvider?.integration_id && loadingProvider?.provider === integration?.provider)
                                ? `Connecting to ${integration.display_name}...`
                                : `Connect`}
                            </button>
                          )}
                        </div>
                        {Object.keys(error || {}).length > 0 ? <p style={{ color: "red" }}>{error?.[integration.provider]}</p> : <p style={{ color: "red" }}>{error}</p>}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            {/* GHL Location Refresh Static Box */}
            <div className="w-[320px] h-[326px] relative !border-[#D9D9D9] rounded border mr-4 mb-4 p-7 bg-[#F8F9FA]">
              <div className="block ">
                <div className="py-[5px] text-[12px] px-4 w-fit h-fit rounded-full bg-[#E3F2FD] text-[#1976D2] ml-auto">
                  GHL System
                </div>
                <div className="flex items-center gap-2 mt-3">
                  <div className="border rounded !border-[#D9D9D9] w-[50px] h-[50px] flex items-center justify-center bg-white">
                    <img
                      src="https://www.google.com/s2/favicons?sz=64&domain_url=gohighlevel.com"
                      alt="GoHighLevel"
                      className="w-[30px] h-[30px]"
                    />
                  </div>
                  <div className="text-[22px] font-bold text-wrap">
                    GoHighLevel
                  </div>
                </div>

                <div className="font-normal mt-3">
                  <h2 className="block leading-[140%] text-[16px]">
                    Refresh GHL Location Data
                  </h2>
                  <p className="text-[12px] text-gray-600 mt-1">
                    Sync latest location information and settings
                  </p>
                </div>
                <a
                  href={'https://www.notion.so/maybetech/How-to-integrate-connect-GoHighLevel-with-your-AI-Agents-and-what-the-integration-can-be-used-for-285e7fe9c4078020b063c52876f09622'}
                  target="_blank"
                  rel="noreferrer"
                  underline="none"
                  style={{
                    fontSize: "12px",
                    display: "flex",
                    alignItems: "center",
                    gap: "4px",
                  }}
                >
                  <span style={{ color: "#b1b1b1" }}>Learn more</span>
                  <OpenInNewIcon
                    fontSize="inherit"
                    sx={{ color: "#b1b1b1" }}
                  />
                </a>
                <div className="absolute bottom-[43px] right-0 left-0 mx-[35px]">
                  {userActiveLocation?.ghl_data && Object.keys(userActiveLocation?.ghl_data)?.length > 0 ? (
                    <div className="flex items-center gap-2 font-normal text-[12px]  mb-4">
                      <span className="w-[10px] h-[10px] rounded-full bg-[#CFF7D3]"></span>
                      <strong style={{ textTransform: "capitalize" }}>
                        {userActiveLocation?.ghl_data?.business?.name}
                      </strong>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2 !font-normal text-[12px] mb-4">
                      <span className="w-[10px] h-[10px] rounded-full bg-[#FDD3D0]"></span>
                      <strong style={{ textTransform: "capitalize" }}>
                        No Account found
                      </strong>
                    </div>
                  )}
                  {userActiveLocation?.ghl_data && Object.keys(userActiveLocation?.ghl_data)?.length > 0
                    ? <div className="flex items-center gap-3">
                      < button
                        onClick={handleGHLLocationRefresh}
                        className="px-2 py-2 text-xs bg-white border !text-nowrap border-black w-full h-[42px] rounded text-black uppercase font-bold"
                      >
                        Refresh Location
                      </button>
                      < button
                        onClick={() => { setConfrimDisconnect(true); setDisconnectProvider({ provider: "GHL", name: 'ghl' }) }}
                        className="px-2 py-2 text-xs bg-[#1E1E1E] w-full h-[42px] rounded text-white uppercase font-bold"
                      >
                        Disconnect
                      </button>

                    </div> :
                    < button
                      onClick={handleGHLLocationRefresh}
                      className={`px-6 py-2 text-xs w-full h-[42px] rounded text-white uppercase font-bold bg-black transition-colors`}
                    >
                      Connect
                    </button>
                  }
                </div>
              </div>
            </div>
          </>
        ) : (
          <div className=" p-5 mb-6">
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <div>
                <h2
                  className="py-2"
                  style={{ fontSize: "20px", fontWeight: "bold" }}
                >
                  Loading...
                </h2>
              </div>
            </div>
          </div>
        )}
        {confirmDisconnect && (
          <div className="modal-zz" onClick={() => setConfrimDisconnect(true)}>
            <div
              className="modal-content-zz position-relative"
              onClick={(e) => e.stopPropagation()}
            >
              <div>
                <h2 className="text-xl font-bold">
                  Do you really want to disconnect {disconnectProvider?.name}?
                </h2>
              </div>
              <div style={{ display: "flex", gap: "20px", marginTop: "20px" }}>
                <button
                  onClick={() => setConfrimDisconnect(false)}
                  aria-label="Send message"
                  disabled={disconnecting}
                  className="px-10 py-2 text-xs bg-red-800 text-white rounded-md uppercase font-bold"
                  style={{
                    backgroundColor: "#4D4D4D",
                    color: "#fff",
                    width: "100%",
                    maxWidth: "300px",
                    textAlign: "center",
                  }}
                >
                  <div className="hover-text">Cancel</div>
                </button>

                <button
                  onClick={disconnectProvider?.provider === 'GHL' ? handleDisconnectGHL : handleDisconnection}
                  className="px-10 py-2 text-xs bg-red-800 text-white rounded-md uppercase font-bold"
                  style={{
                    width: "100%",
                    maxWidth: "300px",
                    textAlign: "center",
                  }}
                  disabled={disconnecting}
                >
                  {disconnecting && (
                    <Icons
                      id="spinner"
                      color="white"
                      style={{ verticalAlign: "text-top", marginRight: "10px" }}
                    />
                  )}
                  Disconnect
                </button>
              </div>
            </div>
          </div>
        )}

        {openTeamModal && (
          <AddUserModal
            isModalOpen={openTeamModal}
            justCloseIt={() => setOpenTeamModal(false)}
            userActiveLocation={userActiveLocation}
            activeplans={activeProduct}
            locationUsers={locationUsers}
            setActiveTab={() =>
              navigate("/app/settings", { state: { tab: "Pricing" } })
            }
          />
        )}
        <Toast message={toast.message} visible={toast.visible} subMessage={toast.subMessage} onClose={() => setToast({ message: "", subMessage: "", visible: false })} />
      </div >
    </>
  );
};

export default NangoIntegration;
